import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import Typography, { TypographyProps } from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import { DreamMuiDialog } from "@portal/app/_components/ui-elements/Mui/DreamMuiDialog";
import { DialogActionButton } from "@portal/app/_components/ui-elements/ConfirmationDialog";
import { Filters } from "@portal/app/_common/types/dreamSearch";

interface LoadingStateFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: Filters;
  clearAllFilters: () => void;
}

export const LoadingStateFilterModal = ({
  isOpen,
  onClose,
  filters,
  clearAllFilters,
}: LoadingStateFilterModalProps) => {
  return (
    <DreamMuiDialog
      open={isOpen}
      onClose={onClose}
      TransitionProps={{ timeout: 0 }}
      BackdropProps={{ timeout: 0 }}
      slotProps={{
        backdrop: {
          timeout: 0,
          style: { transition: "none" },
        },
      }}
    >
      <FilterTitle>
        {/* h2, not the `page_title` variant's default h1 — this labels the panel, not the page. */}
        <FiltersTitleText variant="page_title" component="h2">
          Filters
        </FiltersTitleText>
        <Button
          variant="text"
          size="small"
          color="primary"
          onClick={() => clearAllFilters()}
          disabled={!Object.keys(filters).length}
        >
          Clear all
        </Button>
      </FilterTitle>
      <FilterDialogContent>
        <MobileFullDreamSearchRedirectButton>
          <LoadingSubindustryContainer>
            <LoadingSubindustryTitle sx={{ fontWeight: 600 }}>
              What programs are you looking for?
            </LoadingSubindustryTitle>
            <LoadingSubindustryPillsContainer>
              {Array.from({ length: 3 }).map((_, index) => (
                <LoadingSubindustryPill key={index} variant="rounded" height={40} />
              ))}
            </LoadingSubindustryPillsContainer>
          </LoadingSubindustryContainer>
        </MobileFullDreamSearchRedirectButton>
        <FilterContainer>
          <Divider />
          {/* Class format */}
          <LoadingFilterSection>
            <LoadingFilterTitle variant="h6">Class format</LoadingFilterTitle>
            <LoadingFilterPillsContainer>
              <LoadingPill width={78} />
              <LoadingPill width={58} />
              <LoadingPill width={68} />
            </LoadingFilterPillsContainer>
          </LoadingFilterSection>

          {/* Program type */}
          <LoadingFilterSection>
            <LoadingFilterTitle variant="h6" sx={{ m: 0 }}>
              Program type
            </LoadingFilterTitle>
            <LoadingFilterSearchBox variant="rounded" height={48} />
            <LoadingFilterSubtitle variant="body2">Top programs</LoadingFilterSubtitle>
            <LoadingFilterPillsContainer>
              {[120, 150, 140, 100, 85, 120, 150, 140, 100, 85].map((width, i) => (
                <LoadingPill key={`program-type-pill-${i}`} width={width} />
              ))}
            </LoadingFilterPillsContainer>
          </LoadingFilterSection>

          {/* Payment options */}
          <LoadingFilterSection>
            <LoadingFilterTitle variant="h6">Payment options</LoadingFilterTitle>
            <LoadingFilterPillsContainer>
              <LoadingPill width={100} />
              <LoadingPill width={180} />
            </LoadingFilterPillsContainer>
          </LoadingFilterSection>

          {/* Program duration */}
          <LoadingFilterSection>
            <LoadingFilterTitle variant="h6">Program duration</LoadingFilterTitle>
            <LoadingFilterPillsContainer>
              <LoadingPill width={85} />
              <LoadingPill width={90} />
              <LoadingPill width={70} />
              <LoadingPill width={75} />
            </LoadingFilterPillsContainer>
          </LoadingFilterSection>

          {/* Program level */}
          <LoadingFilterSection>
            <LoadingFilterTitle variant="h6">Program level</LoadingFilterTitle>
            <LoadingFilterPillsContainer>
              <LoadingPill width={135} />
              <LoadingPill width={120} />
              <LoadingPill width={125} />
              <LoadingPill width={130} />
            </LoadingFilterPillsContainer>
          </LoadingFilterSection>
        </FilterContainer>
      </FilterDialogContent>
      <DialogActions>
        <DialogActionButton onClick={onClose}>Done</DialogActionButton>
      </DialogActions>
    </DreamMuiDialog>
  );
};

const LoadingPill = ({ width }: { width: number }) => {
  return <LoadingFilterPill variant="rounded" height={32} width={width} />;
};

// Styled components
const FilterTitle = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding: 0;

  ${({ theme }) =>
    theme.breakpoints.up("md") &&
    `
      padding: 2rem;
      margin-bottom: 0;
  `}
`;

const FiltersTitleText = styled(Typography)<TypographyProps>`
  margin: 0;
`;

const FilterDialogContent = styled(DialogContent)`
  scrollbar-width: none;
  // Hide scrollbar on Safari

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FilterContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  margin-top: 1.5rem;
`;

const MobileFullDreamSearchRedirectButton = styled(Box)`
  .full_dream_search_redirect_button {
    height: 5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

// Loading state styled components
const LoadingSubindustryContainer = styled(Box)`
  background-color: ${({ theme }) => theme.palette.primary.main};
  border-radius: 1rem;
  padding: 1rem;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const LoadingSubindustryTitle = styled(Typography)`
  color: white;
  text-align: center;
  margin-bottom: 1rem;
`;

const LoadingSubindustryPillsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const LoadingSubindustryPill = styled(Skeleton)`
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 20px;
`;

const LoadingFilterSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const LoadingFilterTitle = styled(Typography)`
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const LoadingFilterSubtitle = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-top: 0.5rem;
`;

const LoadingFilterPillsContainer = styled(Box)`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const LoadingFilterPill = styled(Skeleton)`
  border-radius: 18px;
`;

const LoadingFilterSearchBox = styled(Skeleton)`
  border-radius: 24px;
`;
